import React, { ReactElement, useContext, useEffect, useMemo, useReducer } from 'react';
import { useActionCriteria } from '../../utils/useActionProps';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import getClientMutationId from '../../utils/getClientMutationId';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { BinLocation } from '../../graphql/entities';
import { AttributeArray } from '../../components/connectedForm/inputComponents';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { List, ListItem } from '@fluentui/react-components';
import FluentModal from '../../components/modals/FluentModal';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import HeaderTitle from '../../components/connectedForm/HeaderTitle';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { PrimaryButton } from '@fluentui/react';
import { SecondaryButton } from '../../components/connectedForm/Button';
import {
  FilterOperation,
  IAssemblyReceiveRequirements,
  IAssemblyWithReceipt,
  IBinLocation,
  IBulkAssemblyReceiveMutationResult,
  IBulkAssemblyReceiveMutationVariables,
  ILotAttributeValue,
} from '../../graphql/schema/p4types';
import AssemblyListItem from './AssemblyListItem';
import { default as Form } from '../../components/connectedForm';
import { BulkAssemblyReceiveMutation } from '../../graphql/mutations/product/BulkAssemblyReceiveMutation.graphql';

type StateType = {
  assembliesNeedManualConfiguration: IAssemblyWithReceipt[];
  assembliesReadyForAutoReceive: IAssemblyWithReceipt[];
  selectedAssemblyIdx: number;
  visitedAssemblies: Set<number>;
  completeAssemblies: Set<number>;
  binLocations: Array<IBinLocation | null>;
  selectedAssembly: IAssemblyWithReceipt | null;
  hasLotAttributes: boolean;
};

const initialState: StateType = {
  assembliesNeedManualConfiguration: [],
  assembliesReadyForAutoReceive: [],
  selectedAssemblyIdx: -1,
  visitedAssemblies: new Set<number>(),
  completeAssemblies: new Set<number>(),
  binLocations: [],
  selectedAssembly: null,
  hasLotAttributes: false,
};

enum ActionType {
  initialize,
  select,
  unread,
  binlocation,
  lotattribute,
  reset,
}

type Action =
  | {
      type: ActionType.initialize;
      payload: {
        assembliesNeedManualConfiguration: IAssemblyWithReceipt[];
        assembliesReadyForAutoReceive: IAssemblyWithReceipt[];
        binLocations: Array<IBinLocation | null>;
      };
    }
  | { type: ActionType.select; payload: { selectedAssemblyIdx: number } }
  | { type: ActionType.unread; payload: { assemblyNumber: number } }
  | { type: ActionType.binlocation; payload: { binLocation: IBinLocation } }
  | { type: ActionType.lotattribute; payload: { lotAttributeValues: ILotAttributeValue[] } }
  | { type: ActionType.reset };

const canCompleteAndReturn = (state: StateType): StateType => {
  const assemblyWithReceipt = state.selectedAssembly;

  const lotAttributesComplete =
    assemblyWithReceipt?.assembly?.product?.lotAttributes?.length ===
    assemblyWithReceipt?.receipt?.lotAttributeValues?.length;

  const binLocationComplete = assemblyWithReceipt?.receipt?.binLocationId!!;

  if (lotAttributesComplete && binLocationComplete) {
    return {
      ...state,
      completeAssemblies: new Set<number>(state.completeAssemblies).add(assemblyWithReceipt.assembly?.orderNumber),
    };
  } else {
    return state;
  }
};

const reducer = (state: StateType, action: Action): StateType => {
  switch (action.type) {
    case ActionType.initialize:
      return {
        ...state,
        assembliesNeedManualConfiguration: action.payload.assembliesNeedManualConfiguration,
        assembliesReadyForAutoReceive: action.payload.assembliesReadyForAutoReceive,
        binLocations: action.payload.binLocations,
      };
    case ActionType.select:
      const selectedAssembly = state.assembliesNeedManualConfiguration[action.payload.selectedAssemblyIdx];

      return {
        ...state,
        selectedAssemblyIdx: action.payload.selectedAssemblyIdx,
        visitedAssemblies: new Set<number>(state.visitedAssemblies).add(selectedAssembly.assembly?.orderNumber),
        selectedAssembly: selectedAssembly,
        hasLotAttributes: (selectedAssembly?.assembly?.product?.lotAttributes?.length ?? 0) > 0,
      };
    case ActionType.unread:
      const sizeOfVisitedAssemblies = state.visitedAssemblies.size;
      const newVisitedAssemblies = new Set<number>(state.visitedAssemblies);
      newVisitedAssemblies.add(action.payload.assemblyNumber);

      // if number was added then we are toggling the assembly to unread
      if (sizeOfVisitedAssemblies === newVisitedAssemblies.size) {
        newVisitedAssemblies.delete(action.payload.assemblyNumber);
      }
      return {
        ...state,
        visitedAssemblies: newVisitedAssemblies,
      };
    case ActionType.binlocation:
      const updatedBinLocations = [...state.binLocations];
      updatedBinLocations[state.selectedAssemblyIdx] = action.payload.binLocation;

      const newSelectedAssembly = state.selectedAssembly?.receipt
        ? {
            ...state.selectedAssembly,
            receipt: {
              ...state.selectedAssembly.receipt,
              binLocationId: action.payload.binLocation.id,
            },
          }
        : state.selectedAssembly;

      const assembliesNeedManualConfiguration = [...state.assembliesNeedManualConfiguration];
      const newAssemblyReceipt = {
        ...assembliesNeedManualConfiguration[state.selectedAssemblyIdx],
        receipt: {
          ...assembliesNeedManualConfiguration[state.selectedAssemblyIdx].receipt,
          binLocationId: action.payload.binLocation.id,
        },
      };
      assembliesNeedManualConfiguration[state.selectedAssemblyIdx] = newAssemblyReceipt;

      const updatedState = {
        ...state,
        binLocations: updatedBinLocations,
        assembliesNeedManualConfiguration: assembliesNeedManualConfiguration,
        selectedAssembly: newSelectedAssembly,
      };

      return canCompleteAndReturn(updatedState);
    case ActionType.lotattribute:
      const updatedSelectedAssembly = state.selectedAssembly?.receipt
        ? {
            ...state.selectedAssembly,
            receipt: {
              ...state.selectedAssembly.receipt,
              lotAttributeValues: action.payload.lotAttributeValues,
            },
          }
        : state.selectedAssembly;

      const newAssembliesNeedManualConfiguration = [...state.assembliesNeedManualConfiguration];
      const updatedAssemblyReceipt = {
        ...newAssembliesNeedManualConfiguration[state.selectedAssemblyIdx],
        receipt: {
          ...newAssembliesNeedManualConfiguration[state.selectedAssemblyIdx].receipt,
          lotAttributeValues: action.payload.lotAttributeValues,
        },
      };
      newAssembliesNeedManualConfiguration[state.selectedAssemblyIdx] = updatedAssemblyReceipt;

      const newState = {
        ...state,
        assembliesNeedManualConfiguration: newAssembliesNeedManualConfiguration,
        selectedAssembly: updatedSelectedAssembly,
      };

      return canCompleteAndReturn(newState);
    case ActionType.reset:
      return initialState;
    default:
      return state;
  }
};

export const AssemblyPreBulkReceiveModal = ({
  onClose,
  modalOpen,
  preBulkReceiveDetails,
}: {
  onClose?: () => void;
  modalOpen: boolean;
  preBulkReceiveDetails: IAssemblyReceiveRequirements;
}): ReactElement | null => {
  const [modalState, modalDispatch] = useReducer(reducer, initialState);

  const actionProps = useActionCriteria();
  const { reloadData } = useContext(WorkspaceContext);

  useEffect(() => {
    const assemblies = preBulkReceiveDetails.needsManualConfiguration?.filter(
      (assemblyWithReceipt: Maybe<IAssemblyWithReceipt>): assemblyWithReceipt is IAssemblyWithReceipt =>
        assemblyWithReceipt !== null
    );

    const assembliesReady = preBulkReceiveDetails.readyForAutoReceive?.filter(
      (assemblyWithReceipt: Maybe<IAssemblyWithReceipt>): assemblyWithReceipt is IAssemblyWithReceipt =>
        assemblyWithReceipt !== null
    );

    const tempBinLocations: Array<IBinLocation | null> = [];
    assemblies?.forEach((assemblyWithReceipt: IAssemblyWithReceipt) => {
      const warehouse = assemblyWithReceipt?.assembly?.warehouse;
      const pwd = assemblyWithReceipt?.assembly?.product?.productWarehouseDetails?.filter(
        (warehouseDetails) => warehouseDetails?.warehouse?.id === warehouse?.id
      )[0];
      tempBinLocations.push(pwd?.defaultBinLocation ?? null);
    });

    modalDispatch({
      type: ActionType.initialize,
      payload: {
        assembliesNeedManualConfiguration: assemblies || [],
        assembliesReadyForAutoReceive: assembliesReady || [],
        binLocations: tempBinLocations,
      },
    });
  }, [preBulkReceiveDetails.needsManualConfiguration]);

  const handleSelectionChange = (_event: any, data: any) => {
    const selectedOrderNumber = data.selectedItems[0];

    let index = -1;

    if (modalState.assembliesNeedManualConfiguration) {
      const assemblies = modalState.assembliesNeedManualConfiguration;
      for (let i = 0; i < assemblies.length; i++) {
        if (assemblies[i]?.assembly?.orderNumber === selectedOrderNumber) {
          index = i;
          break;
        }
      }
    }

    modalDispatch({
      type: ActionType.select,
      payload: { selectedAssemblyIdx: index },
    });
  };

  const [bulkReceiveMutation] = useBackgroundMutation<
    IBulkAssemblyReceiveMutationResult,
    IBulkAssemblyReceiveMutationVariables
  >(BulkAssemblyReceiveMutation, {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        actionCriteria: actionProps,
        assemblyWithReceipts: [
          ...modalState.assembliesNeedManualConfiguration,
          ...modalState.assembliesReadyForAutoReceive,
        ],
      },
    },
    fetchPolicy: 'network-only',
    onCompleteJob: () => {
      reloadData?.();
    },
  });

  const bulkReceive = () => {
    bulkReceiveMutation({
      onCompleted: () => {
        onClose?.();
        modalDispatch({
          type: ActionType.reset,
        });
      },
      onError: (_error) => {
        modalDispatch({
          type: ActionType.reset,
        });
      },
    });
  };

  const renderForm = (): ReactElement => {
    return (
      <Form.Form>
        <Form.Legend label={`Required Bin Location`} />
        <Form.Row>
          <Form.Fieldset cols={1}>
            <QueryDropdown
              {...BinLocation.dropdown}
              label="Bin Location"
              predefinedFilters={[
                {
                  filterParameters: {
                    field: 'warehouse.id',
                    value: modalState.selectedAssembly?.assembly?.warehouse?.id,
                  },
                  filterOperation: FilterOperation.Equals,
                },
              ]}
              value={modalState.binLocations[modalState.selectedAssemblyIdx]}
              onChange={(value: IBinLocation) => {
                modalDispatch({
                  type: ActionType.binlocation,
                  payload: { binLocation: value },
                });
              }}
              data-testid="binlocation"
              mandatory
            />
          </Form.Fieldset>
        </Form.Row>
        {modalState.hasLotAttributes && (
          <Form.Row>
            <Form.Fieldset cols={1}>
              <Form.Legend label={`Required Lot Attributes`} />
              <AttributeArray
                type="lot"
                value={modalState.selectedAssembly?.receipt?.lotAttributeValues ?? []}
                data={modalState.selectedAssembly?.assembly?.product?.lotAttributes}
                label="Lot attributes"
                onChange={(values: ILotAttributeValue[]) => {
                  modalDispatch({
                    type: ActionType.lotattribute,
                    payload: { lotAttributeValues: normalizeVariables(values, ['type']) },
                  });
                }}
                data-testid="lotAttributes"
                shouldDisplayAttributeReadOnly={(lotAttribute: ILotAttributeValue) =>
                  !lotAttribute.attributeConfig?.generated
                }
              />
            </Form.Fieldset>
          </Form.Row>
        )}
      </Form.Form>
    );
  };

  const readIndicatorClick = (assemblyNumber: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    modalDispatch({
      type: ActionType.unread,
      payload: { assemblyNumber },
    });
  };

  const listItems = useMemo(() => {
    return (
      preBulkReceiveDetails?.needsManualConfiguration
        ?.filter(
          (assemblyWithReceipt: Maybe<IAssemblyWithReceipt>): assemblyWithReceipt is IAssemblyWithReceipt =>
            assemblyWithReceipt !== null
        )
        .map((assemblyWithReceipt: IAssemblyWithReceipt) => {
          return (
            <ListItem
              key={assemblyWithReceipt?.assembly?.orderNumber}
              value={assemblyWithReceipt?.assembly?.orderNumber}
              data-value={assemblyWithReceipt}
              checkmark={null}
            >
              <AssemblyListItem
                assemblyWithReceipt={assemblyWithReceipt}
                processed={modalState.completeAssemblies.has(assemblyWithReceipt?.assembly?.orderNumber)}
                visited={modalState.visitedAssemblies.has(assemblyWithReceipt?.assembly?.orderNumber)}
                isSelected={
                  assemblyWithReceipt?.assembly?.orderNumber === modalState.selectedAssembly?.assembly?.orderNumber
                }
                onReadIndicatorClick={(event) => readIndicatorClick(assemblyWithReceipt?.assembly?.orderNumber, event)}
              />
            </ListItem>
          );
        }) ?? []
    );
  }, [preBulkReceiveDetails, modalState.visitedAssemblies, modalState.completeAssemblies]);

  return (
    <FluentModal isOpen={modalOpen} styles={{ main: { minWidth: '50%', paddingBottom: '60px' } }}>
      <HeaderContainer>
        <HeaderTitle
          value={'Bulk Assembly Receive'}
          subtitle={'More information is required for the following assemblies'}
        />
      </HeaderContainer>
      <div style={{ display: 'flex', minHeight: '400px' }}>
        {/* Left column - List */}
        <div style={{ flex: '0 0 25%', borderRight: '1px solid #e1e1e1' }}>
          <List
            selectionMode="single"
            onSelectionChange={handleSelectionChange}
            selectedItems={[modalState.selectedAssemblyIdx]}
          >
            {listItems}
          </List>
        </div>

        {/* Right column - Form */}
        <div style={{ flex: 1 }}>
          {modalState.selectedAssembly ? (
            renderForm()
          ) : (
            <div style={{ padding: '16px', color: '#666', textAlign: 'center', marginTop: '50px' }}>
              Select an item from the list to view details
            </div>
          )}
        </div>
      </div>
      <ModalBottomButtonContainer>
        <PrimaryButton
          disabled={modalState.completeAssemblies?.size !== modalState.assembliesNeedManualConfiguration?.length}
          onClick={bulkReceive}
        >
          Ok
        </PrimaryButton>
        <SecondaryButton
          onClick={() => {
            onClose?.();
            modalState({
              type: ActionType.reset,
            });
          }}
        >
          Dismiss
        </SecondaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default AssemblyPreBulkReceiveModal;
